import { ArrowRight, BarChart3, ShieldCheck, FileBarChart, CalendarClock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { Link } from "react-router-dom";

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function HeroSection({ onBookingOpen }) {
  const { t } = useLanguage();
  const settings = useSiteSettings();
  const hero = t("hero");
  const areas = t("practiceAreas.items") || [];
  const waClean = (settings.whatsapp || "").replace(/[^\d]/g, "");

  return (
    <section id="hero" data-testid="hero-section" aria-label="PETROCONTROL - Gestión de permisos de autoconsumo y regulación energética" className="relative bg-[#F5F6F3] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-28 pb-16 relative">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-4 items-center min-h-[75vh]">
          <div className="relative z-10 animate-fade-in-left">
            <p className="text-[#B59A65] text-xs uppercase tracking-[0.3em] mb-6 font-semibold">
              {hero.subtitle}
            </p>
            <h1
              data-testid="hero-headline"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-[3.5rem] font-extrabold text-[#152238] leading-[1.1] tracking-[-0.02em] mb-8"
            >
              {hero.headline}
            </h1>
            <p className="text-[#66727B] text-base md:text-lg max-w-lg leading-relaxed mb-10">
              {hero.description}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button
                data-testid="hero-schedule-btn"
                onClick={onBookingOpen}
                className="bg-[#103F38] text-white hover:bg-[#0c332d] rounded-full px-10 py-6 text-sm font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
              >
                {hero.cta} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <a
                href={`https://api.whatsapp.com/send?phone=${waClean}&text=Hola%2C%20me%20interesa%20informaci%C3%B3n%20sobre%20sus%20servicios%20de%20gesti%C3%B3n%20de%20permisos`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white hover:bg-[#1ebe5d] rounded-full px-8 py-3.5 text-sm font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
              >
                <WhatsAppIcon className="h-5 w-5" />
                WhatsApp
              </a>
            </div>
          </div>

          {/* ── Right-side cards ── */}
          <div className="relative flex justify-center lg:justify-end animate-fade-in-right">
            <div className="relative w-full max-w-lg">
              <div className="relative w-full h-auto lg:h-[580px] flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-[#103F38]/10 to-transparent rounded-3xl"></div>
                <div className="relative z-10 grid grid-cols-2 gap-4 p-6 w-full">
                  <Link to="/services/controles-volumetricos" data-testid="hero-card-volumetricos" className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 group cursor-pointer block">
                    <BarChart3 className="h-10 w-10 text-[#103F38] mb-3" />
                    <h3 className="font-bold text-[#152238] text-base mb-1">Controles Volumétricos</h3>
                    <p className="text-xs text-[#66727B] mb-2">Certificados ante SAT y CRE</p>
                    <span className="inline-flex items-center text-xs font-semibold text-[#103F38] group-hover:gap-1.5 gap-1 transition-all">Ver más <ChevronRight className="h-3 w-3" /></span>
                  </Link>
                  <Link to="/services/permisos-autoconsumo" data-testid="hero-card-permisos" className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 group cursor-pointer block">
                    <ShieldCheck className="h-10 w-10 text-[#103F38] mb-3" />
                    <h3 className="font-bold text-[#152238] text-base mb-1">Gestión de Permisos</h3>
                    <p className="text-xs text-[#66727B] mb-2">Autoconsumo y regulación CNE</p>
                    <span className="inline-flex items-center text-xs font-semibold text-[#103F38] group-hover:gap-1.5 gap-1 transition-all">Ver más <ChevronRight className="h-3 w-3" /></span>
                  </Link>
                  <Link to="/services/tramites-cne" data-testid="hero-card-api-cne" className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 col-span-2 group cursor-pointer block">
                    <FileBarChart className="h-10 w-10 text-[#103F38] mb-3" />
                    <h3 className="font-bold text-[#152238] text-lg mb-1">Conexión API para reportes CNE</h3>
                    <p className="text-sm text-[#66727B] mb-2">Envío automatizado de reportes a la Comisión Nacional de Energía</p>
                    <span className="inline-flex items-center text-xs font-semibold text-[#103F38] group-hover:gap-1.5 gap-1 transition-all">Ver más <ChevronRight className="h-3 w-3" /></span>
                  </Link>
                  <Link to="/services/permisos-autoconsumo" data-testid="hero-card-registro-temporal" className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 col-span-2 group cursor-pointer block">
                    <CalendarClock className="h-10 w-10 text-[#103F38] mb-3" />
                    <h3 className="font-bold text-[#152238] text-lg mb-1">Registro Temporal CNE</h3>
                    <p className="text-sm text-[#66727B] mb-2">Te permite cargar combustible en tu tanque hasta el 31 de diciembre de 2026</p>
                    <span className="inline-flex items-center text-xs font-semibold text-[#103F38] group-hover:gap-1.5 gap-1 transition-all">Ver más <ChevronRight className="h-3 w-3" /></span>
                  </Link>
                </div>
                <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-[#103F38]/5 rounded-full blur-3xl"></div>
                <div className="absolute -left-10 -top-10 w-48 h-48 bg-[#103F38]/5 rounded-full blur-3xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div data-testid="practice-marquee" className="bg-[#103F38] py-4 overflow-hidden">
        <div className="animate-marquee flex items-center whitespace-nowrap">
          {[...areas, ...areas].map((area, i) => (
            <span key={`marquee-${area.id}-${i}`} className="flex items-center">
              <span className="text-white font-medium text-lg mx-8 tracking-wide">{area.title}</span>
              <span className="text-white/40 text-xl mx-2">&#10038;</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
